import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import BackLink from '../components/BackLink';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import useUserStore from '../stores/useUserStore';
import ProgressRing from '../components/ui/ProgressRing';
import Progress from '../components/ui/Progress';
import FadeIn from '../components/ui/FadeIn';
import Panel from '../components/ui/Panel';
import { formatEventDate } from '../utils/date-utils';
import PageLayout from '../components/ui/PageLayout';
import LogoSpinner from '../components/ui/LogoSpinner';
import { OrderTypeTag } from './StatusPill';
import CheckRow from '../components/ui/CheckRow';
import { Field } from '../components/ui/Field';

/* ------------------------------------------------------------------ */
/*  Types — mirror firefleet-api/src/sales/fulfillment-template.js    */
/* ------------------------------------------------------------------ */
type ItemType = 'check' | 'qty' | 'text';

interface TemplateItem {
  key: string;
  label: string;
  type: ItemType;
  placeholder?: string;
}

interface TemplateCategory {
  category: string;
  label: string;
  qtySource?: string[];
  items: TemplateItem[];
}

interface FulfillmentCheck {
  id?: string;
  item_key: string;
  status: 'packed' | 'deferred';
  checked_by?: string;
  checked_at: string;
  value_qty?: number | null;
  value_text?: string | null;
  firstname?: string;
  lastname?: string;
}

interface FulfillmentOrder {
  id: string;
  so_number: string;
  order_type: 'sale' | 'lease' | 'rental';
  status: string;
  customer_name?: string;
  org_name?: string;
  ship_date?: string | null;
  delivery_date?: string | null;
  effective_date?: string | null;
  end_date?: string | null;
  notes?: string | null;
  lines?: Array<{ item_type: string; quantity: number }>;
}

interface FulfillmentResponse {
  template: TemplateCategory[];
  totalItems: number;
  autoQuantities: Record<string, number>;
  checks: FulfillmentCheck[];
  order: FulfillmentOrder;
}

interface AuthUser {
  id?: string;
  firstname?: string;
  lastname?: string;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */
const getUserInitials = (user: AuthUser | null | undefined): string => {
  if (!user) return '??';
  return ((user.firstname?.[0] || '') + (user.lastname?.[0] || '')).toUpperCase() || '??';
};

const formatCheckTime = (ts: string | undefined): string => {
  if (!ts) return '';
  const d = new Date(ts);
  const h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
};

/* ------------------------------------------------------------------ */
/*  Fulfillment Item Row                                               */
/* ------------------------------------------------------------------ */
interface FulfillmentItemProps {
  fullKey: string;
  item: TemplateItem;
  check: FulfillmentCheck | undefined;
  autoQty?: number;
  onCheck: (key: string, values: { qty?: number | null; text?: string | null; status?: 'packed' | 'deferred' }) => Promise<void>;
  onDefer: (key: string) => Promise<void>;
  onRemove: (key: string) => Promise<void>;
  user: AuthUser | null;
  animDelay: number;
}

const FulfillmentItem: React.FC<FulfillmentItemProps> = ({
  fullKey, item, check, autoQty, onCheck, onDefer, onRemove, user, animDelay,
}) => {
  const isPacked = check?.status === 'packed';
  const isDeferred = check?.status === 'deferred';
  const isDone = isPacked || isDeferred;

  // For qty items, pre-fill with auto-quantity until the user overrides.
  // Always store as string so the <input> stays controlled and leading zeros
  // don't sneak in from number-to-string conversions.
  const initialQty: string =
    check?.value_qty != null ? String(check.value_qty)
    : item.type === 'qty' && autoQty != null ? String(autoQty)
    : '';
  const [qty, setQty] = useState<string>(initialQty);
  const [text, setText] = useState<string>(check?.value_text ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (check?.value_qty != null) setQty(String(check.value_qty));
    else if (item.type === 'qty' && autoQty != null) setQty(String(autoQty));
    setText(check?.value_text ?? '');
  }, [check?.value_qty, check?.value_text, autoQty, item.type]);

  // Normalize qty input: strip leading zeros, allow empty while typing,
  // reject non-numeric input (belt-and-suspenders alongside type='number').
  const handleQtyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    if (v === '') { setQty(''); return; }
    const n = parseInt(v, 10);
    if (!isNaN(n) && n >= 0) setQty(String(n));
  };

  const handleTap = async () => {
    if (saving) return;
    setSaving(true);
    if (isPacked || isDeferred) {
      await onRemove(fullKey);
    } else {
      const values: { qty?: number | null; text?: string | null; status?: 'packed' | 'deferred' } = { status: 'packed' };
      if (item.type === 'qty') values.qty = qty === '' ? null : parseInt(qty, 10);
      if (item.type === 'text') values.text = text || null;
      await onCheck(fullKey, values);
    }
    setSaving(false);
  };

  const handleLongPress = async () => {
    if (saving || isPacked) return;
    setSaving(true);
    if (isDeferred) {
      await onRemove(fullKey);
    } else {
      await onDefer(fullKey);
    }
    setSaving(false);
  };

  const checkerInitials = check ? ((check.firstname?.[0] || '') + (check.lastname?.[0] || '')).toUpperCase() : '';

  const meta = (
    <>
      {check?.value_qty != null && <span>Qty: {check.value_qty}</span>}
      {check?.value_text && <span className="truncate max-w-[200px]">{check.value_text}</span>}
      {checkerInitials && (
        <>
          {(check?.value_qty != null || check?.value_text) && (
            <span className="w-0.5 h-0.5 rounded-full bg-gray-700" />
          )}
          <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-700/60 text-[8px] font-bold text-on-surface-variant">
            {checkerInitials}
          </span>
          <span>{formatCheckTime(check?.checked_at)}</span>
        </>
      )}
    </>
  );

  const hasFields = !isDone && (item.type === 'qty' || item.type === 'text');

  return (
    <CheckRow
      className="ff-item"
      style={{ animationDelay: `${animDelay}ms` }}
      tone="good"
      checked={isPacked ? true : isDeferred ? 'mixed' : false}
      onToggle={handleTap}
      onHold={isPacked ? undefined : handleLongPress}
      holdHint="Hold to skip"
      holdKeyboard={false}
      busy={saving}
      label={item.label}
      mixedWord="Skipped"
      meta={isDone && (check?.value_qty != null || check?.value_text || checkerInitials) ? meta : undefined}
      trailing={hasFields ? (
        <div className="flex items-center gap-1.5">
          {item.type === 'qty' && (
            <div className="inline-flex items-center gap-1">
              <span className="text-[10px] text-on-surface-variant">QTY</span>
              <Field type="number" inputMode="numeric" min="0" size="sm" aria-label={`${item.label} quantity`} value={qty}
                onChange={handleQtyChange}
                className="!w-14 text-center" />
            </div>
          )}
          {item.type === 'text' && (
            <Field type="text" size="sm" aria-label={`${item.label} note`} value={text} onChange={e => setText(e.target.value)}
              placeholder={item.placeholder || ''}
              className="!w-40" />
          )}
        </div>
      ) : undefined}
    />
  );
};

/* ------------------------------------------------------------------ */
/*  Category Card                                                      */
/* ------------------------------------------------------------------ */
interface CategoryCardProps {
  category: TemplateCategory;
  checks: Map<string, FulfillmentCheck>;
  autoQuantities: Record<string, number>;
  onCheck: FulfillmentItemProps['onCheck'];
  onDefer: FulfillmentItemProps['onDefer'];
  onRemove: FulfillmentItemProps['onRemove'];
  user: AuthUser | null;
  index: number;
}

const CategoryCard: React.FC<CategoryCardProps> = ({
  category, checks, autoQuantities, onCheck, onDefer, onRemove, user, index,
}) => {
  const filled = category.items.filter(item => checks.has(`${category.category}.${item.key}`)).length;
  const total = category.items.length;
  const complete = filled === total;
  const totalQty = autoQuantities[`${category.category}.total`];

  return (
    <Panel
      level={3}
      padding="sm"
      titleStyle="section"
      accent={complete ? 'green' : 'primary'}
      title={
        <>
          {category.label}
          {totalQty != null && totalQty > 0 && (
            <span className="ml-2 font-normal normal-case tracking-normal text-on-surface-variant">
              · {totalQty} from order
            </span>
          )}
        </>
      }
      actions={
        <>
          <ProgressRing value={filled} max={total} size={20} success label={category.label} decorative />
          <span className={`text-[11px] tabular-nums font-medium ${complete ? 'text-green-400' : 'text-on-surface-variant'}`}>
            {filled}/{total}
          </span>
          {complete && (
            <svg className="w-4 h-4 text-green-500/60 ff-check-in" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
        </>
      }
    >
      {/* Items */}
      <div className="px-1 py-1">
        {category.items.map((item, i) => {
          const fullKey = `${category.category}.${item.key}`;
          return (
            <FulfillmentItem
              key={fullKey}
              fullKey={fullKey}
              item={item}
              check={checks.get(fullKey)}
              autoQty={item.key === 'total' ? autoQuantities[fullKey] : undefined}
              onCheck={onCheck}
              onDefer={onDefer}
              onRemove={onRemove}
              user={user}
              animDelay={index * 60 + i * 20}
            />
          );
        })}
      </div>
    </Panel>
  );
};

/* ------------------------------------------------------------------ */
/*  Main Page                                                          */
/* ------------------------------------------------------------------ */
const FulfillmentPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const api = useApi();
  const toast = useToast();
  const user = useUserStore(state => state.user) as AuthUser | null;

  const [template, setTemplate] = useState<TemplateCategory[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [autoQuantities, setAutoQuantities] = useState<Record<string, number>>({});
  const [checks, setChecks] = useState<Map<string, FulfillmentCheck>>(new Map());
  const [order, setOrder] = useState<FulfillmentOrder | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const data = await api.get(`/orders/${id}/fulfillment`, { silent: true }) as FulfillmentResponse;
      setTemplate(data.template);
      setTotalItems(data.totalItems);
      setAutoQuantities(data.autoQuantities || {});
      setOrder(data.order);
      const map = new Map<string, FulfillmentCheck>();
      (data.checks || []).forEach(c => map.set(c.item_key, c));
      setChecks(map);
    } catch {
      toast.error('Failed to load fulfillment data');
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filledCount = checks.size;
  const allDone = filledCount >= totalItems;

  const handleCheck: FulfillmentItemProps['onCheck'] = async (itemKey, values) => {
    const prevValue = checks.get(itemKey);
    setChecks(m => {
      const next = new Map(m);
      next.set(itemKey, {
        item_key: itemKey,
        status: values.status || 'packed',
        checked_by: user?.id,
        firstname: user?.firstname,
        lastname: user?.lastname,
        checked_at: new Date().toISOString(),
        value_qty: values.qty ?? null,
        value_text: values.text ?? null,
      });
      return next;
    });
    try {
      const row = await api.put(`/orders/${id}/fulfillment/${itemKey}`, { body: values, silent: true }) as FulfillmentCheck;
      setChecks(m => { const next = new Map(m); next.set(itemKey, row); return next; });
    } catch {
      setChecks(m => {
        const next = new Map(m);
        if (prevValue) next.set(itemKey, prevValue);
        else next.delete(itemKey);
        return next;
      });
      toast.error('Failed to save');
    }
  };

  const handleDefer: FulfillmentItemProps['onDefer'] = async (itemKey) => {
    const prevValue = checks.get(itemKey);
    setChecks(m => {
      const next = new Map(m);
      next.set(itemKey, {
        item_key: itemKey,
        status: 'deferred',
        checked_by: user?.id,
        firstname: user?.firstname,
        lastname: user?.lastname,
        checked_at: new Date().toISOString(),
      });
      return next;
    });
    try {
      const row = await api.put(`/orders/${id}/fulfillment/${itemKey}`, { body: { status: 'deferred' }, silent: true }) as FulfillmentCheck;
      setChecks(m => { const next = new Map(m); next.set(itemKey, row); return next; });
    } catch {
      setChecks(m => {
        const next = new Map(m);
        if (prevValue) next.set(itemKey, prevValue);
        else next.delete(itemKey);
        return next;
      });
      toast.error('Failed to save');
    }
  };

  const handleRemove: FulfillmentItemProps['onRemove'] = async (itemKey) => {
    const prevValue = checks.get(itemKey);
    setChecks(m => { const next = new Map(m); next.delete(itemKey); return next; });
    try {
      await api.delete(`/orders/${id}/fulfillment/${itemKey}`, { silent: true });
    } catch {
      setChecks(m => {
        const next = new Map(m);
        if (prevValue) next.set(itemKey, prevValue);
        return next;
      });
      toast.error('Failed to save');
    }
  };

  if (loading) {
    return <LogoSpinner label="Loading the fulfillment checklist" />;
  }

  if (!order) {
    return (
      <div className="max-w-lg mx-auto px-4 py-10 text-center">
        <p className="text-on-surface-variant">Order not found</p>
        <BackLink to="/orders" label="Orders" className="mt-2" />
      </div>
    );
  }

  const customerLabel = order.org_name || order.customer_name || '—';
  const dateLabel = order.ship_date
    ? `Ship ${formatEventDate(order.ship_date, 'MMM D')}`
    : order.delivery_date
      ? `Delivery ${formatEventDate(order.delivery_date, 'MMM D')}`
      : order.effective_date
        ? `Effective ${formatEventDate(order.effective_date, 'MMM D')}`
        : null;

  return (
    <PageLayout kind="record" className="pt-5">
      <BackLink to={`/orders/${order.id}`} label="Order" className="mb-3" />
      {/* Header */}
      <div className="sticky top-0 z-20 lg:static flex flex-col lg:flex-row gap-4 mb-5 ff-card-in
                       -mx-4 px-4 sm:-mx-6 sm:px-6 pt-1 pb-4 lg:mx-0 lg:px-0 lg:pt-0 lg:pb-0">
        <Panel label="Order" className="flex-1">
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Link to={`/orders/${order.id}`}
                  className="text-base font-semibold text-white hover:text-primary transition-colors truncate leading-tight font-mono">
                  {order.so_number}
                </Link>
                <OrderTypeTag type={order.order_type} />
              </div>
              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                <span className="text-[12px] text-on-surface-variant truncate">{customerLabel}</span>
                {dateLabel && (
                  <>
                    <span className="w-1 h-1 rounded-full bg-gray-700" />
                    <span className="text-[11px] text-on-surface-variant">{dateLabel}</span>
                  </>
                )}
                <span className="w-1 h-1 rounded-full bg-gray-700" />
                <span className="text-[11px] text-on-surface-variant capitalize">{order.status?.replace(/_/g, ' ')}</span>
              </div>
            </div>
            {/* Progress ring */}
            <div className="flex-shrink-0">
              <ProgressRing value={filledCount} max={totalItems} size={44} success display="percent" label="Fulfillment checklist" />
            </div>
          </div>

          {/* Progress bar — mobile only */}
          <div className="mt-3 lg:hidden">
            <Progress
              value={filledCount}
              max={totalItems}
              label="Fulfillment checklist progress"
              display="count"
              success="Ready"
              size="md"
            />
          </div>

          <p className="text-[10px] text-on-surface-variant mt-2 tracking-wide">
            Tap to check · Long-press to skip items that don&apos;t apply
          </p>
        </Panel>

        {/* Desktop progress panel */}
        <div className="hidden lg:block lg:w-72 flex-shrink-0">
          <Panel label="Checklist progress" className="h-full justify-center">
            <div className="flex flex-col gap-3">
            <Progress
              value={filledCount}
              max={totalItems}
              label="Fulfillment checklist progress"
              display="count"
              size="lg"
            />
            <FadeIn key={allDone ? 'done' : 'open'}>
              {allDone ? (
                <div className="text-center text-sm text-green-400/80 font-medium">
                  Checklist complete
                </div>
              ) : (
                <div className="text-center text-[11px] text-on-surface-variant">
                  {totalItems - filledCount} item{totalItems - filledCount !== 1 ? 's' : ''} remaining
                </div>
              )}
            </FadeIn>
            </div>
          </Panel>
        </div>
      </div>

      {/* Category cards — CSS columns for masonry flow on desktop */}
      <div className="ff-columns">
        {template.map((cat, i) => (
          <div key={cat.category} className="mb-3 break-inside-avoid ff-card-in" style={{ animationDelay: `${i * 60}ms` }}>
            <CategoryCard
              category={cat}
              checks={checks}
              autoQuantities={autoQuantities}
              onCheck={handleCheck}
              onDefer={handleDefer}
              onRemove={handleRemove}
              user={user}
              index={i}
            />
          </div>
        ))}
      </div>

      {/* Animations */}
      <style>{`
        @keyframes ffCardIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .ff-card-in {
          animation: ffCardIn 0.4s ease-out both;
        }
        @keyframes ffCheckIn {
          0% { transform: scale(0) rotate(-20deg); opacity: 0; }
          60% { transform: scale(1.2) rotate(0deg); }
          100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }
        .ff-check-in {
          animation: ffCheckIn 0.3s ease-out;
        }
        @media (min-width: 1024px) {
          .ff-columns {
            columns: 3;
            column-gap: 0.75rem;
          }
        }
      `}</style>
    </PageLayout>
  );
};

export default FulfillmentPage;
